import React, { useEffect, useCallback, useRef } from "react";
import UserCard from "./UserCard";
import usePendingRequests from "./usePendingRequests";
import "./PendingRequestsTab.css";
import { MergeModal } from "../../components/modals";
import { useAuth } from "../../../../../context/AuthContext";

/**
 * PendingRequestsTab displays all pending user requests with potential database matches
 *
 * @param {Object} props
 * @param {Function} props.getAuthHeaders - Function to get authentication headers
 * @param {Function} props.logout - Function to handle logout
 * @returns {JSX.Element}
 */
const PendingRequestsTab = () => {
  // Get auth context
  const { getAuthHeaders, logout } = useAuth();

  // Use the custom hook for pending requests functionality
  const {
    // State
    pendingUsers,
    selectedApprovals,
    loading,
    errorMessage,
    successMessage,
    editableUser,
    editedFields,
    isSaving,
    saveMessage,
    isMergeMode,
    recordsToMerge,
    baseRecordId,
    mergedData,
    groupmeDataForMerge,

    // Functions
    loadPendingRequests,
    loadMatchesForUser,
    handleSelectUserForApproval,
    toggleEditMode,
    handleFieldChange,
    saveUserData,
    startMergeProcess,
    changeBaseRecord,
    updateMergedField,
    selectFieldFromRecord,
    saveMergedRecord,
    cancelMerge,
    handleSubmitApprovals,
    setErrorMessage,
    setSuccessMessage,
  } = usePendingRequests({ getAuthHeaders, logout });

  // Keep track of whether the component is mounted
  const isMounted = useRef(false);

  // Memoize the loadPendingRequests function to prevent re-renders
  const memoizedLoadPendingRequests = useCallback(() => {
    // Only load if the component is mounted to prevent unnecessary API calls
    if (isMounted.current) {
      console.log("Manual refresh of pending requests triggered");
      loadPendingRequests();
    }
  }, [loadPendingRequests]);

  // Load pending requests on mount only (empty dependency array)
  useEffect(() => {
    isMounted.current = true;
    console.log("PendingRequestsTab mounted - initial data load");
    loadPendingRequests();

    // Clean up function
    return () => {
      isMounted.current = false;
      console.log("PendingRequestsTab unmounted");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get count of selected approvals
  const approvalCount = Object.keys(selectedApprovals).length;

  return (
    <div className="pending-requests-tab">
      <div className="pending-requests-header">
        <h2 className="section-title">Pending User Requests</h2>
        <p className="sort-info">Sorted by most recent requests first</p>

        <div className="header-actions">
          <button
            className="refresh-btn"
            onClick={memoizedLoadPendingRequests}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh Requests"}
          </button>
        </div>

        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      {/* Submit Approvals Button */}
      {approvalCount > 0 && (
        <div className="approval-actions">
          <button
            className="submit-approvals-btn"
            onClick={handleSubmitApprovals}
            disabled={loading}
            style={{
              opacity: loading ? 0.7 : 1,
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            Submit Approvals ({approvalCount})
          </button>
        </div>
      )}

      {/* Merge Modal */}
      <MergeModal
        isOpen={isMergeMode}
        records={recordsToMerge}
        baseRecordId={baseRecordId}
        mergedData={mergedData}
        groupmeData={groupmeDataForMerge}
        onBaseRecordChange={changeBaseRecord}
        onFieldChange={updateMergedField}
        onFieldSelect={selectFieldFromRecord}
        isSaving={isSaving}
        saveMessage={saveMessage}
        onSave={saveMergedRecord}
        onCancel={cancelMerge}
      />

      {/* Pending Users List */}
      <div className="pending-users-list">
        {pendingUsers.length === 0 ? (
          <div className="no-pending-requests">
            {loading ? "Loading..." : "No pending requests found"}
          </div>
        ) : (
          pendingUsers.map((pendingUser) => (
            <UserCard
              key={pendingUser.groupmeData.id}
              pendingUser={pendingUser}
              selectedUserId={
                selectedApprovals[pendingUser.groupmeData.id]?.chosenUserId
              }
              onSelectUser={handleSelectUserForApproval}
              onToggleEdit={toggleEditMode}
              editableUser={editableUser}
              editedFields={editedFields}
              onFieldChange={handleFieldChange}
              onSaveUser={saveUserData}
              isSaving={isSaving}
              saveMessage={saveMessage}
              onStartMerge={startMergeProcess}
              onLoadMatches={loadMatchesForUser}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default PendingRequestsTab;
